import * as React from "react";
import {
  ButtonGroup,
  ComboBox,
  Content,
  Dialog,
  Divider,
  Flex,
  Footer,
  Header,
  Heading,
  Item,
} from "@adobe/react-spectrum";

import {
  AtollonButton,
  AtollonButtonDesignType,
} from "../../ui/Button/AtollonButton";

import { ColumnsSetupViewSettings } from "./components/ColumnsSetupViewSettings";
import {
  FilterSetupPlaceholder,
  IFilterControl,
} from "./components/FilterSetupPlaceholder";
import { SortingSetupPlaceholder } from "./components/SortingSetupPlaceholder";
import { Stage4Summary } from "./components/Stage4Summary";

import { NameInfo } from "./components/NamesList";
import { IColumnSection, IColumnItem } from "./components/ColumnsList";
import { ISortingRow } from "./components/SortingTable";

import { useDependency } from "../../../contexts/DependencyProvider";
import { SettingItem } from "../../../services/soap/features/responses/GetFormSettingsResponse";
import { CreateCustomViewsRequest } from "../../../services/soap/features/requests/CreateCustomViewsRequest";
import { UpdateCustomViewsRequest } from "../../../services/soap/features/requests/UpdateCustomViewsRequest";
import {
  CustomView,
  Name,
  Translation,
} from "../../../services/soap/dataObjects/CustomView";
import { SoapUtils } from "../../../utils/SoapUtils";
import { ListProfileRequest } from "../../../services/soap/access/requests/ListProfileRequest";
import { Profile } from "../../../services/soap/access/responses/ListProfileResponse";
import { GetUserRequest } from "../../../services/soap/access/requests/GetUserRequest";

export interface ICustomViewData {
  columnsSetup: {
    names: NameInfo[];
    allColumns: IColumnSection[];
    selectedColumns: IColumnSection[];
    viewAccess: "organization" | "user" | "profile";
    selectedProfileId: string | null;
    desktopOrientation: "Detail" | "Full";
    tabletOrientation: "Detail" | "Full";
    mobileOrientation?: "Detail" | "Full"; // Deprecated, kept for backward compatibility
  };
  filterSetup: IFilterControl[];
  sortingSetup: ISortingRow[];
}

export interface ICustomViewDialogProps {
  formSettings?: SettingItem[];
  applicationId: string;
  onCancel: () => void;
  onSave: (data: ICustomViewData) => void;
  editView?: CustomView;
  fpaTypeCategory?: string;
  isDuplicate?: boolean;
}

const getInitialViewData = (): ICustomViewData => ({
  columnsSetup: {
    names: [{ name: "", languageId: null }],
    allColumns: [],
    selectedColumns: [],
    viewAccess: "organization",
    selectedProfileId: null,
    desktopOrientation: "Detail",
    tabletOrientation: "Full",
  },
  filterSetup: [],
  sortingSetup: [],
});

// Parse definition JSON from existing CustomView
interface ParsedDefinition {
  columns: Array<{
    label: string;
    type?: string;
    itemname: string;
    isVisible: boolean;
    isFilter: boolean;
  }>;
  filters: Array<{
    name: string;
    dataType: string;
    itemname: string;
    columnLabel: string;
    filterInfo: any[];
  }>;
  sorting: Array<{ column: string; itemname: string; level: string }>;
  viewAccess: "organization" | "user" | "profile";
  selectedProfileId?: string;
  desktopOrientation?: "Detail" | "Full" | "table view" | "cards view"; // Support both old and new formats for backward compatibility
  mobileOrientation?: "Detail" | "Full" | "table view" | "cards view"; // Deprecated, kept for backward compatibility
  tabletOrientation?: "Detail" | "Full" | "table view" | "cards view"; // Support both old and new formats for backward compatibility
  tabletHorizontalOrientation?: "Detail" | "Full" | "table view" | "cards view"; // Deprecated, kept for backward compatibility
  tabletVerticalOrientation?: "Detail" | "Full" | "table view" | "cards view"; // Deprecated, kept for backward compatibility
  names?: Array<{ lang: string; name: string }>;
}

const parseDefinition = (
  definitionStr: string | undefined
): ParsedDefinition | null => {
  if (!definitionStr) return null;
  try {
    return JSON.parse(definitionStr) as ParsedDefinition;
  } catch {
    return null;
  }
};

const buildViewDataFromEditView = (
  editView: CustomView,
  formSettings: SettingItem[]
): ICustomViewData => {
  const parsed = parseDefinition(editView.definition);

  // Build names - Priority 1: Parse from JSON definition, Priority 2: Fallback to SOAP NAME
  let names: NameInfo[] = [];
  
  // Priority 1: Parse names from JSON definition if available
  if (parsed?.names && parsed.names.length > 0) {
    names = parsed.names.map((n) => ({
      name: n.name,
      languageId: n.lang,
    }));
  } else {
    // Priority 2: Fallback to SOAP NAME.TRANSLATION[]
    names = editView.NAME?.TRANSLATION?.map((t) => ({
      name: t.text || "",
      languageId: t.short || null,
    })) || [{ name: editView.name || "", languageId: null }];
  }

  if (names.length === 0) {
    names.push({ name: "", languageId: null });
  }

  // Build columns from parsed definition
  const selectedColumnItems: IColumnItem[] =
    parsed?.columns?.map((col) => {
      // Find matching column from formSettings by itemname
      const matchingFormSetting = formSettings.find(
        (item) => item.itemname === col.itemname
      );
      return {
        id: matchingFormSetting ? String(matchingFormSetting.id) : "",
        label: col.label,
        type: col.type,
        itemname: col.itemname,
        isVisible: col.isVisible,
        isFilter: col.isFilter,
      };
    }) || [];

  const selectedColumnItemnames = new Set(
    selectedColumnItems.map((c) => c.itemname)
  );

  // Build all columns from formSettings, excluding selected ones, grouped by groupBy
  const allColumnItemsFiltered = formSettings.filter(
    (item) => !selectedColumnItemnames.has(item.itemname)
  );
  const allColumnsGrouped = transformFormSettingsToColumns(
    allColumnItemsFiltered
  );

  // Build filters from parsed definition
  const filterSetup: IFilterControl[] =
    parsed?.filters?.map((f) => {
      // Find matching column from formSettings by itemname
      const matchingFormSetting = formSettings.find(
        (item) => item.itemname === f.itemname
      );
      return {
        name: f.columnLabel,
        dataType: f.dataType,
        data: {
          id: matchingFormSetting ? String(matchingFormSetting.id) : "",
          label: f.columnLabel,
          itemname: f.itemname,
          isVisible: true,
          isFilter: true,
        },
        filterInfo: f.filterInfo || [],
      };
    }) || [];

  // Build sorting from parsed definition
  const sortingSetup: ISortingRow[] =
    parsed?.sorting?.map((s) => ({
      column: s.column,
      itemname: s.itemname,
      level: s.level as "Ascending" | "Descending",
    })) || [];

  // Extract selectedProfileId from parsed definition or assignTo field
  const selectedProfileId =
    parsed?.selectedProfileId ||
    (editView.assignTo ? String(editView.assignTo) : null);

  // Determine viewAccess - check if assignTo exists to infer 'profile'
  let viewAccess: "organization" | "user" | "profile" =
    (parsed?.viewAccess as "organization" | "user" | "profile") ||
    (editView.level as "organization" | "user" | "profile") ||
    "organization";
  if (editView.assignTo && !parsed?.viewAccess) {
    viewAccess = "profile";
  }

  // Extract orientation from parsed definition or use defaults
  // Handle backward compatibility: convert old 'table view'/'cards view' to 'Detail'/'Full'
  const desktopOrientationRaw = parsed?.desktopOrientation || "Detail";
  const desktopOrientation: "Detail" | "Full" =
    desktopOrientationRaw === "table view" || desktopOrientationRaw === "Detail"
      ? "Detail"
      : "Full";

  // For backward compatibility: if tabletOrientation is not set, try tabletHorizontalOrientation, tabletVerticalOrientation, or mobileOrientation
  const mobileOrientationRaw = parsed?.mobileOrientation || "Full";
  const defaultTabletOrientation: "Detail" | "Full" =
    mobileOrientationRaw === "cards view" || mobileOrientationRaw === "Full"
      ? "Full"
      : "Detail";

  const tabletOrientationRaw =
    parsed?.tabletOrientation ||
    parsed?.tabletHorizontalOrientation ||
    parsed?.tabletVerticalOrientation ||
    mobileOrientationRaw;
  const tabletOrientation: "Detail" | "Full" =
    tabletOrientationRaw === "table view" || tabletOrientationRaw === "Detail"
      ? "Detail"
      : tabletOrientationRaw === "cards view" || tabletOrientationRaw === "Full"
      ? "Full"
      : defaultTabletOrientation;

  return {
    columnsSetup: {
      names,
      allColumns: allColumnsGrouped,
      selectedColumns:
        selectedColumnItems.length > 0
          ? [{ id: "1", label: "Static Fields", items: selectedColumnItems }]
          : [],
      viewAccess,
      selectedProfileId,
      desktopOrientation,
      tabletOrientation,
    },
    filterSetup,
    sortingSetup,
  };
};

// Transform NameInfo[] to NAME with TRANSLATION[] array
export const transformNamesToName = (nameInfos: NameInfo[]): Name | undefined => {
  // Filter out entries with empty names
  const validNames = nameInfos.filter(
    (info) => info.name && info.name.trim() !== ""
  );

  if (validNames.length === 0) {
    return undefined;
  }

  const translations: Translation[] = validNames.map((info) => ({
    text: info.name,
    short: info.languageId || undefined,
  }));

  return {
    TRANSLATION: translations,
  };
};

// Build definition JSON string from viewData
export const buildDefinition = (viewData: ICustomViewData): string => {
  // Extract column labels and itemnames from selectedColumns
  const columns: Array<{
    label: string;
    type?: string;
    itemname: string;
    isVisible: boolean;
    isFilter: boolean;
  }> = [];
  viewData.columnsSetup.selectedColumns.forEach((section) => {
    section.items?.forEach((item) => {
      columns.push({
        label: item.label,
        type: item.type,
        itemname: item.itemname,
        isVisible: item.isVisible ?? false,
        isFilter: item.isFilter ?? false,
      });
    });
  });

  // Serialize filter controls
  const filters = viewData.filterSetup.map((filter) => ({
    name: filter.data.itemname,
    dataType: filter.dataType,
    itemname: filter.data.itemname,
    columnLabel: filter.data.label,
    filterInfo: filter.filterInfo,
  }));

  // Serialize sorting rows
  const sorting = viewData.sortingSetup.map((row) => ({
    column: row.column,
    itemname: row.itemname,
    level: row.level,
  }));

  // Serialize names from NameInfo[] to JSON array format
  const names = viewData.columnsSetup.names
    .filter((info) => info.name && info.name.trim() !== "" && info.languageId)
    .map((info) => ({ lang: info.languageId!, name: info.name }));

  const definition = {
    columns,
    filters,
    sorting,
    viewAccess: viewData.columnsSetup.viewAccess,
    selectedProfileId: viewData.columnsSetup.selectedProfileId || undefined,
    desktopOrientation: viewData.columnsSetup.desktopOrientation,
    tabletOrientation: viewData.columnsSetup.tabletOrientation,
    names,
  };

  return JSON.stringify(definition);
};

// Helper function to extract group name from otherparams XML string
const extractGroupName = (otherparams: string | undefined | null): string => {
  if (!otherparams || !otherparams.trim()) {
    return "Others";
  }

  try {
    // Wrap XML string in Root tag before parsing
    const wrappedXml = `<Root>${otherparams}</Root>`;
    const parsed = SoapUtils.parseXmlString2(wrappedXml);

    // Extract groupBy value from Root.groupBy
    const groupBy = parsed?.Root?.groupBy;

    // Return group name if valid, otherwise default to 'Others'
    if (groupBy && typeof groupBy === "string" && groupBy.trim()) {
      return groupBy.trim();
    }

    return "Others";
  } catch (error) {
    // If parsing fails, default to 'Others'
    console.warn("Failed to parse otherparams XML:", error);
    return "Others";
  }
};

// Transform SettingItem[] from API response to IColumnSection[]
const transformFormSettingsToColumns = (
  settingItems: SettingItem[]
): IColumnSection[] => {
  if (!settingItems || settingItems.length === 0) {
    return [];
  }

  // Group items by groupBy value from otherparams
  const groupedItems = new Map<string, IColumnItem[]>();
  const groupOrder: string[] = []; // Maintain order of groups as encountered

  settingItems.forEach((item) => {
    // Check if this is a filter-only column (coltype starts with 'filter.')
    const isFilterOnlyColumn = item.coltype?.startsWith("filter.") === true;
    // Filter-only columns should always be invisible
    const isVisible = isFilterOnlyColumn ? false : item.visibility == 1;
    const transformedItem: IColumnItem = {
      id: String(item.id),
      label: item.caption && item.caption.trim() ? item.caption : item.name,
      isVisible,
      isFilter: isVisible, // Set isFilter based on visibility
      type: item.coltype || undefined,
      itemname: item.itemname,
    };

    const groupName = extractGroupName(item.otherparams);

    if (!groupedItems.has(groupName)) {
      groupedItems.set(groupName, []);
      // Add to order array, but ensure 'Others' appears last
      if (groupName === "Others") {
        groupOrder.push(groupName);
      } else {
        // Insert before 'Others' if it exists, otherwise append
        const othersIndex = groupOrder.indexOf("Others");
        if (othersIndex !== -1) {
          groupOrder.splice(othersIndex, 0, groupName);
        } else {
          groupOrder.push(groupName);
        }
      }
    }

    groupedItems.get(groupName)!.push(transformedItem);
  });

  // Ensure 'Others' is last if it exists
  if (groupOrder.includes("Others")) {
    groupOrder.splice(groupOrder.indexOf("Others"), 1);
    groupOrder.push("Others");
  }

  // Convert grouped items to IColumnSection[]
  const sections: IColumnSection[] = groupOrder.map((groupName, index) => ({
    id: String(index + 1),
    label: groupName,
    items: groupedItems.get(groupName) || [],
  }));

  return sections;
};

// Helper type to track items with their section information
interface ItemWithSection {
  item: IColumnItem;
  sectionId: string;
  sectionLabel: string;
}

// Helper function to find and remove items from sections, preserving section info
const removeItemsFromSections = (
  sections: IColumnSection[],
  itemIds: string[]
): { updatedSections: IColumnSection[]; removedItems: ItemWithSection[] } => {
  const removedItems: ItemWithSection[] = [];
  const itemIdsSet = new Set(itemIds);

  const updatedSections = sections
    .map((section) => {
      const remainingItems: IColumnItem[] = [];
      section.items?.forEach((item) => {
        if (itemIdsSet.has(item.id)) {
          // Preserve section information with the item
          removedItems.push({
            item: { ...item }, // Create a copy to avoid reference issues
            sectionId: section.id,
            sectionLabel: section.label,
          });
        } else {
          remainingItems.push(item);
        }
      });
      return {
        ...section,
        items: remainingItems,
      };
    })
    .filter((section) => section.items && section.items.length > 0);

  return { updatedSections, removedItems };
};

// Helper function to add items to sections, maintaining section structure
const addItemsToSections = (
  sections: IColumnSection[],
  itemsWithSections: ItemWithSection[]
): IColumnSection[] => {
  if (itemsWithSections.length === 0) return sections;

  // Group items by their original section
  const itemsBySection = new Map<
    string,
    { label: string; items: IColumnItem[] }
  >();

  itemsWithSections.forEach(({ item, sectionId, sectionLabel }) => {
    if (!itemsBySection.has(sectionId)) {
      itemsBySection.set(sectionId, { label: sectionLabel, items: [] });
    }
    itemsBySection.get(sectionId)!.items.push(item);
  });

  // Create a map of existing sections by ID for quick lookup
  const existingSectionsMap = new Map<string, IColumnSection>();
  sections.forEach((section) => {
    existingSectionsMap.set(section.id, section);
  });

  // Build updated sections array
  const updatedSections: IColumnSection[] = [...sections];

  // Process each section group
  itemsBySection.forEach((sectionData, sectionId) => {
    const existingSection = existingSectionsMap.get(sectionId);

    if (existingSection) {
      // Section exists, add items to it
      const sectionIndex = updatedSections.findIndex((s) => s.id === sectionId);
      if (sectionIndex !== -1) {
        updatedSections[sectionIndex] = {
          ...updatedSections[sectionIndex],
          items: [
            ...(updatedSections[sectionIndex].items || []),
            ...sectionData.items,
          ],
        };
      }
    } else {
      // Section doesn't exist, create new section
      updatedSections.push({
        id: sectionId,
        label: sectionData.label,
        items: sectionData.items,
      });
    }
  });

  return updatedSections;
};

export function CustomViewDialog(props: ICustomViewDialogProps) {
  const { featureService, store, accessService } = useDependency();
  const { fpaTypeCategory } = props;
  const isEditMode = !!props.editView;
  const [currentStage, setCurrentStage] = React.useState<number>(1);
  const [viewData, setViewData] = React.useState<ICustomViewData>(() => {
    if (props.editView && props.formSettings) {
      return buildViewDataFromEditView(props.editView, props.formSettings);
    }
    return getInitialViewData();
  });
  const [filterComboBoxValue, setFilterComboBoxValue] = React.useState<
    string | null
  >(null);
  const [isNamesValid, setIsNamesValid] = React.useState<boolean>(isEditMode);
  const [profiles, setProfiles] = React.useState<Profile[]>([]);
  const [currentUserProfileId, setCurrentUserProfileId] = React.useState<
    string | null
  >(null);

  // Validate form - check names and profile selection if needed
  const isFormValid = React.useMemo(() => {
    if (!isNamesValid) return false;
    if (
      viewData.columnsSetup.viewAccess === "profile" &&
      !viewData.columnsSetup.selectedProfileId
    ) {
      return false;
    }
    return true;
  }, [
    isNamesValid,
    viewData.columnsSetup.viewAccess,
    viewData.columnsSetup.selectedProfileId,
  ]);

  // Fetch profiles list on component mount
  React.useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await accessService.listProfile(
          new ListProfileRequest(store.Server, store.SessionId, "")
        );
        if (response?.PROFILE) {
          setProfiles(response.PROFILE);
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
      }
    };

    fetchProfiles();
  }, [accessService, store.Server, store.SessionId]);

  // Fetch current user profile on component mount
  React.useEffect(() => {
    const fetchCurrentUserProfile = async () => {
      try {
        const response = await accessService.getUser(
          new GetUserRequest(
            store.Server,
            store.SessionId,
            store.UserId.toString()
          )
        );
        if (response?.USER?.profile) {
          setCurrentUserProfileId(String(response.USER.profile));
        }
      } catch (error) {
        console.error("Error fetching current user profile:", error);
      }
    };

    fetchCurrentUserProfile();
  }, [accessService, store.Server, store.SessionId, store.UserId]);

  // Set default profile when viewAccess is 'profile' and in create mode
  React.useEffect(() => {
    if (
      !isEditMode &&
      viewData.columnsSetup.viewAccess === "profile" &&
      currentUserProfileId &&
      !viewData.columnsSetup.selectedProfileId
    ) {
      setViewData((prev) => ({
        ...prev,
        columnsSetup: {
          ...prev.columnsSetup,
          selectedProfileId: currentUserProfileId,
        },
      }));
    }
  }, [
    isEditMode,
    currentUserProfileId,
    viewData.columnsSetup.viewAccess,
    viewData.columnsSetup.selectedProfileId,
  ]);

  // Transform formSettings prop when it changes (only for create mode)
  React.useEffect(() => {
    if (isEditMode) return; // Skip for edit mode - already initialized

    if (!props.formSettings || props.formSettings.length === 0) {
      setViewData((prev) => ({
        ...prev,
        columnsSetup: {
          ...prev.columnsSetup,
          allColumns: [],
        },
      }));
      return;
    }

    const transformedColumns = transformFormSettingsToColumns(
      props.formSettings
    );
    setViewData((prev) => ({
      ...prev,
      columnsSetup: {
        ...prev.columnsSetup,
        allColumns: transformedColumns,
      },
    }));
  }, [props.formSettings, isEditMode]);

  // Handle columns setup data changes
  const handleColumnsSetupChange = React.useCallback(
    (columnsSetupData: ICustomViewData["columnsSetup"]) => {
      setViewData((prev) => ({
        ...prev,
        columnsSetup: columnsSetupData,
      }));
    },
    []
  );

  // Handle profile selection change
  const handleProfileChange = React.useCallback((profileId: string | null) => {
    setViewData((prev) => ({
      ...prev,
      columnsSetup: {
        ...prev.columnsSetup,
        selectedProfileId: profileId,
      },
    }));
  }, []);

  // Handle move to selected (from all columns to selected columns)
  const handleMoveToSelected = React.useCallback((itemIds: string[]) => {
    if (itemIds.length === 0) return;

    setViewData((prev) => {
      const { updatedSections: updatedAllColumns, removedItems } =
        removeItemsFromSections(prev.columnsSetup.allColumns, itemIds);

      const updatedSelectedColumns = addItemsToSections(
        prev.columnsSetup.selectedColumns,
        removedItems
      );

      return {
        ...prev,
        columnsSetup: {
          ...prev.columnsSetup,
          allColumns: updatedAllColumns,
          selectedColumns: updatedSelectedColumns,
        },
      };
    });
  }, []);

  // Handle move to all (from selected columns to all columns)
  const handleMoveToAll = React.useCallback((itemIds: string[]) => {
    if (itemIds.length === 0) return;

    setViewData((prev) => {
      const { updatedSections: updatedSelectedColumns, removedItems } =
        removeItemsFromSections(prev.columnsSetup.selectedColumns, itemIds);

      const updatedAllColumns = addItemsToSections(
        prev.columnsSetup.allColumns,
        removedItems
      );

      return {
        ...prev,
        columnsSetup: {
          ...prev.columnsSetup,
          allColumns: updatedAllColumns,
          selectedColumns: updatedSelectedColumns,
        },
      };
    });
  }, []);

  // Handle sorting setup data changes
  const handleSortingSetupChange = React.useCallback(
    (sortingData: ISortingRow[]) => {
      setViewData((prev) => ({
        ...prev,
        sortingSetup: sortingData,
      }));
    },
    []
  );

  // Handle filter setup data changes
  const handleFilterSetupChange = React.useCallback(
    (filters: IFilterControl[]) => {
      setViewData((prev) => ({
        ...prev,
        filterSetup: filters,
      }));
    },
    []
  );

  // Navigation handlers
  const handleNext = React.useCallback(() => {
    if (currentStage < 4) {
      setCurrentStage((prev) => prev + 1);
    }
  }, [currentStage]);

  const handlePrevious = React.useCallback(() => {
    if (currentStage > 1) {
      setCurrentStage((prev) => prev - 1);
    }
  }, [currentStage]);

  const handleSave = React.useCallback(async () => {
    try {
      // Validate required data - at least one name with non-empty text
      const validNames = viewData.columnsSetup.names.filter(
        (info) => info.name && info.name.trim() !== ""
      );

      if (validNames.length === 0) {
        console.error(
          "Error: At least one name with non-empty text is required"
        );
        // TODO: Show error message to user (toast or error state)
        return;
      }

      // Transform viewData to CustomView format
      const name = transformNamesToName(viewData.columnsSetup.names);
      if (!name) {
        console.error("Error: Failed to transform names");
        // TODO: Show error message to user (toast or error state)
        return;
      }

      const definition = buildDefinition(viewData);

      const customView: CustomView = {
        ...(isEditMode && props.editView?.id ? { id: props.editView.id } : {}),
        NAME: name,
        definition: definition,
        level: viewData.columnsSetup.viewAccess,
        applicationId: props.applicationId,
        priority:
          isEditMode && props.editView?.priority
            ? props.editView.priority
            : "10",
        ...(viewData.columnsSetup.viewAccess === "profile" &&
        viewData.columnsSetup.selectedProfileId
          ? { assignTo: viewData.columnsSetup.selectedProfileId }
          : {}),
      };

      // Call appropriate API based on mode
      let response;
      if (isEditMode && props.editView?.id) {
        response = await featureService.updateCustomViews(
          new UpdateCustomViewsRequest(store.Server, store.SessionId, [
            customView,
          ])
        );
      } else {
        response = await featureService.createCustomViews(
          new CreateCustomViewsRequest(store.Server, store.SessionId, [
            customView,
          ])
        );
      }

      // Handle success
      if (response?.result === "OK") {
        props.onSave(viewData);
        // Dialog will be closed by parent component
      } else {
        console.error(
          `Error ${isEditMode ? "updating" : "creating"} custom view:`,
          response
        );
        // TODO: Show error message to user (toast or error state)
      }
    } catch (error) {
      console.error(
        `Error ${isEditMode ? "updating" : "creating"} custom view:`,
        error
      );
      // TODO: Show error message to user (toast or error state)
      // Keep dialog open on error
    }
  }, [
    props,
    viewData,
    featureService,
    store.Server,
    store.SessionId,
    isEditMode,
  ]);

  // Get all column items from both allColumns and selectedColumns
  const allColumnItems = React.useMemo(() => {
    const items: IColumnItem[] = [];

    // Extract items from allColumns
    viewData.columnsSetup.allColumns.forEach((section) => {
      section.items?.forEach((item) => {
        items.push(item);
      });
    });

    // Extract items from selectedColumns
    viewData.columnsSetup.selectedColumns.forEach((section) => {
      section.items?.forEach((item) => {
        items.push(item);
      });
    });

    return items;
  }, [viewData.columnsSetup.allColumns, viewData.columnsSetup.selectedColumns]);

  // Get available items for dropdown (exclude items already used as filters)
  const availableFilterItems = React.useMemo(() => {
    const usedFilterItemnames = new Set(
      viewData.filterSetup.map((filter) => filter.data.itemname)
    );
    return allColumnItems.filter(
      (item) => !usedFilterItemnames.has(item.itemname)
    );
  }, [allColumnItems, viewData.filterSetup]);

  // Handle filter selection from ComboBox
  const handleFilterSelection = React.useCallback(
    (key: string | null) => {
      if (!key) {
        setFilterComboBoxValue(null);
        return;
      }

      // Find the selected column item from all column items by itemname
      const selectedItem = allColumnItems.find((item) => item.itemname === key);
      if (!selectedItem) {
        setFilterComboBoxValue(null);
        return;
      }

      // Create a new filter control
      const newFilter: IFilterControl = {
        name: selectedItem.label,
        dataType: selectedItem.type || "text", // Use type from column item, default to text
        data: selectedItem,
        filterInfo: [],
      };

      // Add the new filter to the list
      handleFilterSetupChange([...viewData.filterSetup, newFilter]);

      // Reset ComboBox selection
      setFilterComboBoxValue(null);
    },
    [allColumnItems, viewData.filterSetup, handleFilterSetupChange]
  );

  // Render current stage content
  const renderStageContent = () => {
    switch (currentStage) {
      case 1:
        return (
          <ColumnsSetupViewSettings
            data={viewData.columnsSetup}
            onChange={handleColumnsSetupChange}
            onMoveToSelected={handleMoveToSelected}
            onMoveToAll={handleMoveToAll}
            onValidationChange={setIsNamesValid}
            profiles={profiles}
            selectedProfileId={viewData.columnsSetup.selectedProfileId}
            onProfileChange={handleProfileChange}
          />
        );
      case 2:
        return (
          <FilterSetupPlaceholder
            filters={viewData.filterSetup}
            onFiltersChange={handleFilterSetupChange}
            availableItems={allColumnItems}
            fpaTypeCategory={fpaTypeCategory}
          />
        );
      case 3:
        return (
          <SortingSetupPlaceholder
            selectedColumns={viewData.columnsSetup.selectedColumns}
            sortingData={viewData.sortingSetup}
            onSortingChange={handleSortingSetupChange}
          />
        );
      case 4:
        return (
          <Stage4Summary
            data={viewData}
            profiles={profiles}
            onDataChange={setViewData}
          />
        );
      default:
        return null;
    }
  };

  // Get dialog title based on current stage
  const getDialogTitle = (): string => {
    let prefix: string;
    if (props.isDuplicate) {
      prefix = "Add new";
    } else if (isEditMode) {
      prefix = "Edit Custom View";
    } else {
      prefix = "Custom View";
    }
    switch (currentStage) {
      case 1:
        return `${prefix}: Columns Setup`;
      case 2:
        return `${prefix}: Filter Setup`;
      case 3:
        return `${prefix}: Sorting Setup`;
      case 4:
        return `${prefix}: Summary`;
      default:
        return prefix;
    }
  };

  // Render navigation buttons
  const renderNavigationButtons = () => {
    if (currentStage === 1) {
      return (
        <Flex direction="row" gap={10}>
          <AtollonButton
            onPress={props.onCancel}
            designType={AtollonButtonDesignType.LINK}
          >
            Cancel
          </AtollonButton>
          <AtollonButton
            onPress={handleNext}
            designType={AtollonButtonDesignType.LINK}
            isDisabled={!isFormValid}
          >
            Next
          </AtollonButton>
        </Flex>
      );
    } else if (currentStage === 4) {
      return (
        <Flex direction="row" gap={10}>
          <AtollonButton
            onPress={handlePrevious}
            designType={AtollonButtonDesignType.LINK}
          >
            Previous
          </AtollonButton>
          <AtollonButton
            onPress={handleSave}
            designType={AtollonButtonDesignType.LINK}
          >
            Save
          </AtollonButton>
        </Flex>
      );
    } else {
      return (
        <Flex direction="row" gap={10}>
          <AtollonButton
            onPress={handlePrevious}
            designType={AtollonButtonDesignType.LINK}
          >
            Previous
          </AtollonButton>
          <AtollonButton
            onPress={handleNext}
            designType={AtollonButtonDesignType.LINK}
          >
            Next
          </AtollonButton>
        </Flex>
      );
    }
  };

  return (
    <Dialog width="60vw" UNSAFE_style={{ backgroundColor: "var(--white)" }}>
      <Heading>{getDialogTitle()}</Heading>
      <Header>Stage {currentStage} of 4</Header>
      <Divider />
      <ButtonGroup>{renderNavigationButtons()}</ButtonGroup>
      <Content>{renderStageContent()}</Content>
      {currentStage === 2 && (
        <Footer>
          <Flex direction="row" gap={10} width="100%">
            <ComboBox
              aria-label="Filter"
              width="size-3000"
              selectedKey={filterComboBoxValue}
              onSelectionChange={(key) =>
                handleFilterSelection(key as string | null)
              }
            >
              {availableFilterItems.map((item) => (
                <Item key={item.itemname} textValue={item.label}>
                  {item.label}
                </Item>
              ))}
            </ComboBox>
          </Flex>
        </Footer>
      )}
    </Dialog>
  );
}
